import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getJourney, updateStep } from "@/lib/journeyApi";
import { toast } from "sonner";

function StepRow({ step, onToggle, onNotes }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(step.notes || "");
  const done = step.status === "done";

  const saveNotes = async () => {
    try {
      await onNotes(step.key, notes);
      toast.success("Notes saved");
    } catch {
      toast.error("Could not save notes");
    }
  };

  return (
    <div
      data-testid={`journey-step-${step.key}`}
      className={`border border-zinc-800 rounded-sm transition-colors ${
        done ? "bg-yellow-400/5 border-yellow-400/40" : "bg-zinc-950 hover:border-zinc-700"
      }`}
    >
      <div className="flex items-start gap-4 p-4">
        <button
          data-testid={`step-toggle-${step.key}`}
          onClick={() => onToggle(step.key, done ? "todo" : "done")}
          aria-label="toggle step"
          className={`mt-0.5 h-6 w-6 shrink-0 border rounded-sm font-mono text-xs flex items-center justify-center transition-colors ${
            done
              ? "bg-yellow-400 border-yellow-400 text-yellow-950"
              : "border-zinc-700 text-transparent hover:border-yellow-400"
          }`}
        >
          ✓
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className={`font-semibold text-base leading-snug ${done ? "text-zinc-400 line-through" : "text-white"}`}>
              {step.title}
            </h3>
            {done && step.completed_at && (
              <span className="font-mono text-[10px] uppercase tracking-wider text-yellow-400">
                done · {new Date(step.completed_at).toLocaleDateString()}
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{step.description}</p>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            {step.action_url && (
              <a
                data-testid={`step-action-${step.key}`}
                href={step.action_url}
                target={step.action_url.startsWith("http") ? "_blank" : "_self"}
                rel="noreferrer"
                className="font-mono text-xs uppercase tracking-wider px-3 py-1.5 border border-yellow-400/60 text-yellow-400 hover:bg-yellow-400/10 rounded-sm transition-colors"
              >
                ▸ {step.action_label}
              </a>
            )}
            <button
              onClick={() => setOpen((o) => !o)}
              className="font-mono text-xs uppercase tracking-wider text-zinc-500 hover:text-white"
            >
              {open ? "▾ Hide notes" : "▸ Notes"}
            </button>
          </div>
          {open && (
            <div className="mt-3 flex flex-col gap-2">
              <textarea
                data-testid={`step-notes-${step.key}`}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Drop links, drafts, blockers…"
                className="w-full bg-black border border-zinc-800 rounded-sm p-3 text-sm text-zinc-200 font-mono focus:outline-none focus:border-yellow-400"
              />
              <button
                onClick={saveNotes}
                className="self-end font-mono text-xs uppercase tracking-wider px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-yellow-400 hover:text-yellow-400 rounded-sm"
              >
                Save notes
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PhaseSection({ phase, index, total, defaultOpen, onToggle, onNotes }) {
  const [open, setOpen] = useState(defaultOpen);
  const doneInPhase = phase.steps.filter((s) => s.status === "done").length;
  const pct = phase.steps.length
    ? Math.round((doneInPhase / phase.steps.length) * 100)
    : 0;

  return (
    <section
      data-testid={`phase-${phase.phase_key}`}
      className={`border rounded-sm overflow-hidden transition-colors ${
        open ? "border-zinc-700 bg-zinc-950" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
      }`}
    >
      <button
        data-testid={`phase-toggle-${phase.phase_key}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full text-left p-5 flex items-center gap-4 group"
      >
        <span className="font-mono text-yellow-400 text-lg w-6 text-center shrink-0">
          {open ? "▾" : "▸"}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400 mb-1">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-white leading-tight group-hover:text-yellow-400 transition-colors">
            {phase.phase_title}
          </h2>
          {!open && (
            <p className="text-zinc-500 text-sm mt-1 line-clamp-1">
              {phase.phase_subtitle}
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-1">
            {doneInPhase} / {phase.steps.length}
          </div>
          <div className="w-24 h-1 bg-zinc-900 rounded-sm overflow-hidden">
            <div
              className="h-full bg-yellow-400 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5">
          <p className="text-zinc-400 text-sm mb-4 leading-relaxed">
            {phase.phase_subtitle}
          </p>
          <div className="space-y-2">
            {phase.steps.map((step) => (
              <StepRow
                key={step.key}
                step={step}
                onToggle={onToggle}
                onNotes={onNotes}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default function Journey() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getJourney());
    } catch {
      toast.error("Could not load journey");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (key, status) => {
    try {
      await updateStep(key, { status });
      await load();
    } catch {
      toast.error("Could not update step");
    }
  };

  const handleNotes = async (key, notes) => {
    await updateStep(key, { notes });
    await load();
  };

  if (loading || !data) {
    return (
      <div className="px-6 py-10 font-mono text-sm text-zinc-500">
        [recon] loading playbook<span className="blink">_</span>
      </div>
    );
  }

  const pct = data.total_steps ? Math.round((data.done_steps / data.total_steps) * 100) : 0;
  const phaseIdx = data.phases.findIndex((p) => p.steps.some((s) => s.status !== "done"));
  const currentPhase = phaseIdx === -1 ? data.phases.length : phaseIdx + 1;
  const currentPhaseKey = phaseIdx === -1 ? data.phases[0].phase_key : data.phases[phaseIdx].phase_key;

  return (
    <div data-testid="journey-page" className="px-6 lg:px-10 py-8 max-w-[1200px] mx-auto">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
        // CREATOR JOURNEY · {user?.name?.split(" ")[0] || "Operator"}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-white leading-none mb-4">
        Step by step guide to launch your faceless YouTube channel.
      </h1>
      <p className="text-zinc-400 text-base max-w-2xl mb-8 leading-relaxed">
        Your step-by-step playbook. Run a recon scan, pick a niche, ship 3 videos,
        grow to monetization, then earn. Tick each step as you go.
      </p>

      {/* Progress strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 mb-10 rounded-sm overflow-hidden">
        <Stat label="Steps complete" value={`${data.done_steps} / ${data.total_steps}`} />
        <Stat label="Overall progress" value={`${pct}%`} />
        <Stat label="Current phase" value={`${currentPhase} / ${data.phases.length}`} />
        <Stat label="Status" value={pct === 100 ? "MONETIZED" : "IN-FLIGHT"} />
      </div>

      <div
        data-testid="journey-progress-bar"
        className="h-1 bg-zinc-900 mb-12 overflow-hidden rounded-sm"
      >
        <div
          className="h-full bg-yellow-400 transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="space-y-4">
        {data.phases.map((phase, i) => (
          <PhaseSection
            key={phase.phase_key}
            phase={phase}
            index={i}
            total={data.phases.length}
            defaultOpen={phase.phase_key === currentPhaseKey}
            onToggle={handleToggle}
            onNotes={handleNotes}
          />
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-black p-5">
      <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
        {label}
      </div>
      <div className="font-mono font-bold text-2xl text-yellow-400 tracking-tight">
        {value}
      </div>
    </div>
  );
}
